'use client';

import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions {
  threshold?: number;
  root?: Element | null;
  rootMargin?: string;
}

interface UseInViewReturn {
  ref: (element: Element | null) => void;
  inView: boolean;
}

export function useInView({ threshold = 0, root = null, rootMargin = '0px' }: UseInViewOptions = {}): UseInViewReturn {
  const [inView, setInView] = useState(false);
  const currentElement = useRef<Element | null>(null);

  const observer = useRef<IntersectionObserver | null>(null);

  const observe = (element: Element | null) => {
    if (currentElement.current) {
      unobserve();
    }

    if (element) {
      observer.current = new IntersectionObserver(
        ([entry]) => {
          setInView(entry.isIntersecting);
        },
        {
          threshold,
          root,
          rootMargin,
        },
      );

      currentElement.current = element;
      observer.current.observe(element);
    }
  };

  const unobserve = () => {
    if (currentElement.current && observer.current) {
      observer.current.unobserve(currentElement.current);
      currentElement.current = null;
    }
  };

  useEffect(() => {
    return () => {
      unobserve();
    };
  }, []);

  return {
    ref: observe,
    inView,
  };
}
